import React from "react";
import { Link } from "react-router-dom";
import { FiArrowDown, FiArrowRight } from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";

function About() {
  const fallbackProduct = productData[0];
  const findProduct = (predicate) => productData.find(predicate) || fallbackProduct;
  const leadProduct = findProduct((product) => product.gender === "Women" && product.category === "Dresses" && product.tags.includes("new-in"));
  const mensShirt = findProduct((product) => product.gender === "Men" && product.category === "Shirts");
  const layerProduct = findProduct((product) => product.category === "Jackets");
  const linenOnePiece = findProduct((product) => product.category === "Jumpsuits");
  const preorderDress = findProduct((product) => product.category === "Dresses" && product.tags.includes("pre-order") && product.id !== leadProduct.id);
  const saleDress = findProduct((product) => product.category === "Dresses" && product.tags.includes("sale"));
  const tailoredBottom = findProduct((product) => product.category === "Trousers");
  const mensKnit = findProduct((product) => product.gender === "Men" && product.category === "Knitwear");

  const principles = [
    {
      number: "01",
      title: "Read the material first",
      copy: "Each product states its fibre composition, hand-feel considerations and care needs before a size is added to the bag.",
      image: linenOnePiece.images[1],
      alt: `${linenOnePiece.name} linen jumpsuit product views`,
    },
    {
      number: "02",
      title: "See how it is built",
      copy: "Front, back and close-detail photography makes the shape, seam lines, pockets and textile surface easier to understand.",
      image: mensShirt.images[2],
      alt: `${mensShirt.name} linen shirt product views`,
    },
    {
      number: "03",
      title: "Mark pre-orders clearly",
      copy: "A pre-order status means the item is not ready for immediate dispatch. We do not display a date that has not been confirmed.",
      image: preorderDress.images[1],
      alt: `${preorderDress.name} dress product views`,
    },
  ];

  const process = [
    ["01", "Check the fibre", "Composition affects feel, washing, drying and the way a garment changes with wear."],
    ["02", "Compare the fit", "Available sizes, fit language and construction details sit together on every product page."],
    ["03", "Review the purpose", "Use notes explain whether a piece suits layering, warm days, commuting or occasions."],
    ["04", "Plan the care", "Specific washing cautions help avoid heat, abrasion, colour transfer and wet-fibre damage."],
  ];

  return (
    <main className="about-page">
      <section className="about-hero" aria-labelledby="about-title">
        <div className="about-hero__mosaic">
          <figure className="about-hero__frame about-hero__frame--lead">
            <img src={leadProduct.images[1]} alt={`${leadProduct.name} dress back view`} width="1024" height="1536" />
          </figure>
          <figure className="about-hero__frame about-hero__frame--top">
            <img src={mensShirt.images[1]} alt={`${mensShirt.name} shirt back view`} width="1024" height="1536" />
          </figure>
          <figure className="about-hero__frame about-hero__frame--bottom">
            <img src={layerProduct.images[1]} alt={`${layerProduct.name} overshirt back view`} width="1024" height="1536" />
          </figure>
        </div>
        <div className="about-hero__content">
          <p className="eyebrow">{siteConfig.brandName} / Our story</p>
          <h1 id="about-title">Details first.<br />Clothes to<br />repeat.</h1>
          <p>{siteConfig.tagline}</p>
          <a className="about-hero__scroll" href="#about-story">Discover our story <FiArrowDown aria-hidden="true" /></a>
        </div>
      </section>

      <section className="about-intro page-shell" id="about-story" aria-labelledby="about-intro-title">
        <div>
          <p className="eyebrow">What the store stands for</p>
          <h2 id="about-intro-title">A smaller gap between looking and knowing.</h2>
        </div>
        <div className="about-intro__copy">
          <p className="about-intro__lead">{siteConfig.description}</p>
          <p>This {siteConfig.business.region}-operated storefront focuses on ordinary wardrobe decisions: material, use, size, fit, care, price and delivery terms.</p>
          <p>Where a fact has not been confirmed—such as a factory, carrier, warehouse or certification—we leave it out instead of turning uncertainty into a claim.</p>
        </div>
      </section>

      <section className="about-story" aria-labelledby="about-story-title">
        <div className="about-story__image">
          <img src={saleDress.images[1]} alt={`${saleDress.name} dress back view`} width="1024" height="1536" loading="lazy" />
        </div>
        <div className="about-story__copy">
          <p className="eyebrow">Our point of view</p>
          <h2 id="about-story-title">A catalogue should answer useful questions.</h2>
          <p>{siteConfig.displayName} presents each garment with multiple views and a specific record of colour, size, material, construction, care and cautions. The shopping flow then shows the {siteConfig.business.currency} total before {siteConfig.commerce.paymentPlatform} payment.</p>
          <blockquote>“Clarity is part of the product.”</blockquote>
          <span>{siteConfig.logo.symbol} &nbsp; {siteConfig.tagline}</span>
        </div>
      </section>

      <section className="about-principles" aria-labelledby="about-principles-title">
        <header className="about-principles__heading page-shell">
          <div><p className="eyebrow">Our method</p><h2 id="about-principles-title">Useful context,<br />piece by piece.</h2></div>
          <p>These three commitments describe what this storefront can verify today, without borrowing another retailer's supply-chain story.</p>
        </header>
        <div className="about-principles__grid page-shell">
          {principles.map((principle) => (
            <article className="about-principle" key={principle.number}>
              <div className="about-principle__image"><img src={principle.image} alt={principle.alt} width="1024" height="1536" loading="lazy" /><span aria-hidden="true">{principle.number}</span></div>
              <h3>{principle.title}</h3><p>{principle.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-process page-shell" aria-labelledby="about-process-title">
        <div className="about-process__heading"><p className="eyebrow">How to read a product</p><h2 id="about-process-title">From fibre facts to everyday use.</h2></div>
        <ol className="about-process__list">
          {process.map(([number, title, copy]) => <li key={number}><span>{number}</span><div><h3>{title}</h3><p>{copy}</p></div></li>)}
        </ol>
      </section>

      <section className="about-values" aria-labelledby="about-values-title">
        <div className="about-values__statement page-shell"><p className="eyebrow">The checks we return to</p><h2 id="about-values-title">A practical filter for every page.</h2></div>
        <div className="about-values__grid page-shell">
          <article><strong>{siteConfig.logo.symbol}</strong><h3>Identity</h3><p>One original brand system, without copied logos or campaign language.</p></article>
          <article><strong>01</strong><h3>Source of truth</h3><p>Central configuration keeps region, currency, email and time zone aligned.</p></article>
          <article><strong>03</strong><h3>Product views</h3><p>Front, back and material detail for every catalogue item.</p></article>
          <article><strong>∞</strong><h3>Repeat use</h3><p>Care and caution notes support more informed everyday wear.</p></article>
        </div>
      </section>

      <section className="about-makers" aria-labelledby="about-makers-title">
        <div className="about-makers__collage">
          <img className="about-makers__image about-makers__image--large" src={tailoredBottom.images[1]} alt={`${tailoredBottom.name} trousers back view`} width="1024" height="1536" loading="lazy" />
          <img className="about-makers__image about-makers__image--small" src={mensKnit.images[1]} alt={`${mensKnit.name} knit back view`} width="1024" height="1536" loading="lazy" />
        </div>
        <div className="about-makers__copy">
          <p className="eyebrow">Evidence before origin claims</p><h2 id="about-makers-title">Unknown is not a marketing story.</h2>
          <p>The project does not confirm a manufacturing country, factory, warehouse or certification. Those details are therefore absent from product pages and policies.</p>
          <p>If verified sourcing information is supplied later, it should be added to the central product record and checked across About, Product, Shipping and policy pages before publication.</p>
          <Link className="text-link" to="/shop">Meet the collection <FiArrowRight aria-hidden="true" /></Link>
        </div>
      </section>

      <section className="about-cta" aria-labelledby="about-cta-title">
        <img src={preorderDress.images[2]} alt={`${preorderDress.name} dress material detail`} width="1024" height="1536" loading="lazy" />
        <div className="about-cta__overlay" />
        <div className="about-cta__content">
          <p className="eyebrow">Browse with context</p><h2 id="about-cta-title">Choose with the details in view.</h2><p>{siteConfig.tagline}</p>
          <div><Link className="button-light" to="/shop">Explore the collection</Link><Link className="about-cta__link" to="/contact">Talk to our team</Link></div>
        </div>
      </section>
    </main>
  );
}

export default About;
